import React, { useCallback } from "react";
import { SectionReference } from "domains/assets/components/AssetZoom/Aside/SectionReference";
import { SCHEDULERS } from "domains/inference/constants/Schedulers";
import useManagePresets from "domains/inference/hooks/useManagePresets";
import { getIsModelFlux } from "domains/models/utils";
import { mapPresetsToAssets } from "domains/models/utils/mapPresetsToAssets";
import { mapPresetsToInferences } from "domains/models/utils/mapPresetsToInferences";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { AsideListItem, AsideTitledText } from "domains/ui/components/Aside";
import ButtonWithModal from "domains/ui/components/ButtonWithModal";
import {
  GetModelPresetsByModelIdApiResponse,
  GetModelsByModelIdApiResponse,
} from "infra/api/generated/api";

import { VStack } from "@chakra-ui/react";

const DefaultPresetDisplay = ({
  preset,
  model,
}: {
  preset: GetModelPresetsByModelIdApiResponse["presets"][0];
  model: GetModelsByModelIdApiResponse["model"];
}) => {
  const { selectedTeam } = useTeamContext();
  const { removeAllPresets, isRemoveAllPresetsLoading } = useManagePresets();

  const handleRemoveAllPresets = useCallback(async () => {
    await removeAllPresets(preset.modelId);
  }, [preset.modelId, removeAllPresets]);

  const asset = mapPresetsToAssets([preset])[0];
  const inference = mapPresetsToInferences([preset])[0];

  return (
    <VStack align="stretch" spacing={2}>
      {asset.metadata.prompt && (
        <AsideTitledText title="Prompt" value={asset?.metadata.prompt} />
      )}
      {asset?.metadata.negativePrompt && (
        <AsideTitledText
          title="Negative Prompt"
          value={asset.metadata.negativePrompt}
        />
      )}
      <AsideListItem
        label="Dimensions"
        value={`${asset.metadata.width} x ${asset.metadata.height} px`}
      />
      <AsideListItem
        label="Steps"
        value={inference?.parameters.numInferenceSteps ?? "-"}
      />
      <AsideListItem
        label="Guidance"
        value={inference?.parameters.guidance ?? "-"}
      />
      {!getIsModelFlux(model) && (
        <AsideListItem
          label="Scheduler"
          value={
            (inference?.parameters.scheduler &&
              SCHEDULERS[inference.parameters.scheduler]) ||
            "Default (optimal)"
          }
        />
      )}
      <SectionReference
        inference={inference}
        asset={asset}
        withImageLinks={false}
      />
      {selectedTeam.id === preset.ownerId && (
        <ButtonWithModal
          w="full"
          variant="secondary"
          modalHeader="Reset Default Inference Parameters"
          modalBody="Are you sure you want to reset the default parameters?"
          modalColorScheme="danger"
          onConfirm={handleRemoveAllPresets}
          isLoading={isRemoveAllPresetsLoading}
          isModalConfirmButtonLoading={isRemoveAllPresetsLoading}
        >
          Reset Inference Parameters
        </ButtonWithModal>
      )}
    </VStack>
  );
};

export default DefaultPresetDisplay;
